import logger from '../utils/logger'
import i18n from '../i18n'

const getBaseUrl = () => {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL
  if (import.meta.env.PROD) {
    return '/v1'
  }
  return 'http://localhost:8080/v1'
}
const API_BASE_URL = getBaseUrl()

function getFriendlyErrorMessage(status, fallback) {
  const { t } = i18n.global
  const key = `api_errors.${status}`
  const message = t(key)
  
  // If translation exists (not returning the key itself), use it
  if (message !== key) {
    return message
  }
  
  return fallback || t('api_errors.generic_error', { status })
}

export function clearTokens() {
  clearTokenExpiryCache()
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

function getCookie(name) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(';').shift()
  return null
}

const addJitter = (delay) => delay + Math.random() * delay * 0.3

const PROACTIVE_REFRESH_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes in ms

let tokenExpiryCache = null
let refreshPromise = null
let expiryPromise = null

async function getTokenExpiry() {
  if (tokenExpiryCache !== null) {
    return tokenExpiryCache
  }
  
  if (expiryPromise) {
    return expiryPromise
  }
  
  expiryPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/token-expiry`, {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        tokenExpiryCache = new Date(data.expiresAt).getTime()
        return tokenExpiryCache
      }
      if (response.status === 401) {
        tokenExpiryCache = null
        return null
      }
    } catch (err) {
      logger.warn('Failed to get token expiry:', err)
    }
    return null
  })()
  
  const result = await expiryPromise
  expiryPromise = null
  return result
}

export function clearTokenExpiryCache() {
  tokenExpiryCache = null
}
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__clearTokenExpiryCache = clearTokenExpiryCache
}

async function proactiveRefresh() {
  const expiry = await getTokenExpiry()
  if (!expiry) {
    return false
  }
  
  const now = Date.now()
  const timeUntilExpiry = expiry - now
  
  if (timeUntilExpiry > PROACTIVE_REFRESH_THRESHOLD_MS) {
    return false
  }
  
  if (refreshPromise) {
    await refreshPromise
    return true
  }
  
  logger.info('Proactive token refresh triggered...')
  
  try {
    const { useAuthStore } = await import('../stores/authStore')
    const authStore = useAuthStore()
    authStore.setRefreshing()
  } catch {
    // Auth store not available
  }
  
  // Set refreshPromise BEFORE starting async work
  // to prevent race conditions where concurrent requests create multiple refreshes
  refreshPromise = (async () => {
    try {
      const refreshHeaders = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }

      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: refreshHeaders,
        credentials: 'include'
      })
      
      if (response.ok) {
        clearTokenExpiryCache()
        await fetchCSRFToken()

        try {
          const { useAuthStore } = await import('../stores/authStore')
          const authStore = useAuthStore()
          authStore.setActive()
        } catch {
          // Auth store not available
        }

        logger.info('Proactive token refresh successful')
        return true
      }

      if (response.status === 401) {
        clearTokens()
        clearTokenExpiryCache()
        try {
          const { useAuthStore } = await import('../stores/authStore')
          const authStore = useAuthStore()
          authStore.setExpired()
        } catch {
          // Auth store not available
        }
        return false
      }
    } catch (err) {
      logger.error('Proactive token refresh failed:', err)
    }

    return false
  })()
  
  const result = await refreshPromise
  refreshPromise = null
  return result
}

export async function request(endpoint, options = {}) {
  const {
    method = 'GET',
    headers: customHeaders = {},
    body,
    maxRetries = 3,
    skipCSRFRetry = false,
    requiresAuth = true
  } = options

  if (requiresAuth && method !== 'OPTIONS') {
    await proactiveRefresh()
  }

  const buildHeaders = () => {
    const headers = {
      ...customHeaders,
      'X-Requested-With': 'XMLHttpRequest'
    }

    if (body && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase()) && !options.isFormData) {
      headers['Content-Type'] = 'application/json'
    }

    if (!['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) {
      const csrfToken = getCookie('csrf_token')
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }
    }

    return headers
  }

  const attemptRequest = async (retries = maxRetries, delay = 1000, shouldSkipCSRFRetry = false, refreshAttempted = false) => {
    const headers = buildHeaders()
    
    /** @type {RequestInit} */
    const config = {
      method,
      headers,
      credentials: 'include',
      ...(body && { body })
    }

    let response
    try {
      response = await fetch(`${API_BASE_URL}${endpoint}`, config)
    } catch {
      if (retries > 0) {
        logger.warn(`Network error, retrying in ${delay}ms... (${retries} attempts left)`)
        await sleep(addJitter(delay))
        return attemptRequest(retries - 1, delay * 2, shouldSkipCSRFRetry)
      }
      const err = new Error(i18n.global.t('api_errors.network_error'))
      err.isNetworkError = true
      throw err
    }

    if (response.status === 401) {
      if (endpoint !== '/auth/refresh' && !refreshAttempted) {
        if (refreshPromise) {
          const refreshed = await refreshPromise
          if (refreshed) {
            return attemptRequest(retries, delay, shouldSkipCSRFRetry, true)
          }
        } else {
          // Assign the promise synchronously so any concurrent 401 that lands
          // in the same microtask awaits THIS refresh instead of launching a
          // second /auth/refresh POST. Clearing happens in finally, after the
          // promise has fully resolved — guaranteeing that every awaiting
          // caller observes the same result before the mutex releases.
          refreshPromise = (async () => {
            logger.info('Attempting token refresh...')
            try {
              const refreshResponse = await fetch(`${API_BASE_URL}/auth/refresh`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest'
                },
                credentials: 'include'
              })

              if (refreshResponse.ok) {
                clearTokenExpiryCache()
                await fetchCSRFToken()
                return true
              }

              if (refreshResponse.status === 401) {
                try {
                  const { queryClient } = await import('../plugins/vueQuery')
                  queryClient.cancelQueries({ type: 'active' })
                } catch {
                  // Query client not available (e.g., in tests)
                }
              }
              return false
            } finally {
              refreshPromise = null
            }
          })()

          const refreshed = await refreshPromise
          if (refreshed) {
            return attemptRequest(retries, delay, shouldSkipCSRFRetry, true)
          }
        }
      }

      clearTokens()
      clearTokenExpiryCache()

      const { useAuthStore } = await import('../stores/authStore')
      const authStore = useAuthStore()
      authStore.setExpired()

      const err = new Error(i18n.global.t('api_errors.unauthorized'))
      err.status = 401
      throw err
    }

    if (response.status === 429) {
      if (retries > 0) {
        logger.warn(`Rate limited. Retrying in ${delay}ms...`)
        await sleep(delay)
        return attemptRequest(retries - 1, delay * 2, shouldSkipCSRFRetry)
      }
      const err = new Error(i18n.global.t('api_errors.429'))
      err.status = 429
      throw err
    }

    if (response.status === 403 && !shouldSkipCSRFRetry) {
      const errorData = await response.json().catch(() => ({}))
      if (errorData.code === 'CSRF_TOKEN_INVALID' || errorData.code === 'CSRF_TOKEN_MISSING') {
        logger.warn('CSRF token invalid, fetching new token and retrying...')
        try {
          await fetchCSRFToken()
          return attemptRequest(retries, delay, true)
        } catch (csrfErr) {
          logger.error('Failed to refresh CSRF token:', csrfErr)
        }
      }
    }

    if (response.status === 304) {
      const err = new Error(i18n.global.t('api_errors.not_modified'))
      err.status = 304
      throw err
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))

      let message = errorData.message || errorData.error || getFriendlyErrorMessage(response.status)

      if (errorData.details && typeof errorData.details === 'string') {
        message += ` (${errorData.details})`
      } else if (errorData.details && typeof errorData.details === 'object') {
        const details = Object.entries(errorData.details)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join(', ')
        if (details) message = details
      }

      const err = new Error(message)
      err.status = response.status
      err.code = errorData.code || 'UNKNOWN_ERROR'
      err.data = errorData
      err.requestId = errorData.request_id
      err.method = method
      err.url = endpoint
      if (body) {
        try {
          err.requestBody = JSON.parse(body)
        } catch {
          err.requestBody = body
        }
      }

      throw err
    }

    if (response.status === 204) {
      return null
    }

    const data = await response.json()
    return transformResponseData(data)
  }

  return attemptRequest(maxRetries, 1000, skipCSRFRetry)
}

// Helper to scale ratings from backend (2-10) to frontend (1-5)
function scaleRatingDown(val) {
  if (typeof val === 'number') {
    return val / 2
  }
  return val
}

// Recursively transform response data to scale down ratings
function transformResponseData(data) {
  if (Array.isArray(data)) {
    return data.map(item => transformResponseData(item))
  }
  
  if (data !== null && typeof data === 'object') {
    const transformed = {}
    for (const [key, value] of Object.entries(data)) {
      if (['rating', 'averageRating', 'average_rating', 'userRating', 'user_rating'].includes(key) && typeof value === 'number') {
        transformed[key] = scaleRatingDown(value)
      } else if (key === 'value' && typeof value === 'number' && data.target_type && data.target_id) { // likely a vote object
        transformed[key] = scaleRatingDown(value)
      } else if (typeof value === 'object' && value !== null) {
        transformed[key] = transformResponseData(value)
      } else {
        transformed[key] = value
      }
    }
    return transformed
  }
  
  return data
}

export function get(endpoint, options = {}) {
  const { params, ...requestOptions } = options

  let url = endpoint
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString()
    url = `${endpoint}?${queryString}`
  }

  return request(url, { method: 'GET', ...requestOptions })
}

export function post(endpoint, data, options = {}) {
  return request(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    ...options
  })
}

let imageSignCache = new Map()
const SIGN_CACHE_MAX_SIZE = 100

export async function signImageUrl(originalUrl, width = 400, format = 'webp') {
  const cacheKey = `${originalUrl}|${width}|${format}`
  
  if (imageSignCache.has(cacheKey)) {
    return imageSignCache.get(cacheKey)
  }

  try {
    const response = await post('/images/sign', {
      url: originalUrl,
      width,
      format
    }, { requiresAuth: false })

    if (response?.url) {
      if (imageSignCache.size >= SIGN_CACHE_MAX_SIZE) {
        const firstKey = imageSignCache.keys().next().value
        imageSignCache.delete(firstKey)
      }
      imageSignCache.set(cacheKey, response.url)
      return response.url
    }
  } catch (error) {
    logger.warn('Failed to sign image URL:', error)
  }

  return null
}

export function put(endpoint, data, options = {}) {
  return request(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
    ...options
  })
}

export function del(endpoint, data, options = {}) {
  return request(endpoint, {
    method: 'DELETE',
    ...(data && { body: JSON.stringify(data) }),
    ...options
  })
}

export function upload(endpoint, formData, options = {}) {
  return request(endpoint, {
    method: 'POST',
    body: formData,
    isFormData: true,
    ...options
  })
}

export function fetchStream(endpoint, callbacks = {}, options = {}) {
  const { onOpen, onData, onError, onDone } = callbacks
  const { params, headers: customHeaders = {}, ...fetchOptions } = options

  const controller = new AbortController()
  const signal = controller.signal

  let url = `${API_BASE_URL}${endpoint}`
  if (params && Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    ).toString()
    url = `${url}?${queryString}`
  }

  const headers = {
    'Accept': 'text/event-stream',
    'Cache-Control': 'no-cache',
    'Connection': 'keep-alive',
    ...customHeaders
  }

  fetch(url, { headers, signal, credentials: 'include', ...fetchOptions })
    .then(async response => {
      if (!response.ok) {
        throw new Error(`SSE Connection failed: ${response.status}`)
      }

      if (onOpen) onOpen()

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let currentEvent = {
        type: 'message',
        data: null,
        id: null
      }

      const processEvent = () => {
        if (currentEvent.data !== null) {
          let parsedData = currentEvent.data
          // Try to parse JSON string, fallback to raw string
          try {
            parsedData = JSON.parse(currentEvent.data)
          } catch {
            // Keep raw string if not valid JSON
          }
          if (onData) onData(currentEvent.type, parsedData)
        }
        currentEvent = { type: 'message', data: null, id: null }
      }

      while (true) {
        const { value, done } = await reader.read()

        if (done) {
          processEvent()
          if (onDone) onDone()
          break
        }

        const chunk = decoder.decode(value, { stream: true })
        buffer += chunk

        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.trim() === '') {
            processEvent()
            continue
          }

          if (line.startsWith('event:')) {
            processEvent()
            currentEvent.type = line.substring(6).trim() || 'message'
            continue
          }

          if (line.startsWith('id:')) {
            currentEvent.id = line.substring(3).trim()
            continue
          }

          if (line.startsWith('retry:')) {
            continue
          }

          if (line.startsWith('data:')) {
            const dataStr = line.substring(5)
            if (currentEvent.data === null) {
              currentEvent.data = dataStr.trim()
            } else {
              currentEvent.data += '\n' + dataStr
            }
            continue
          }

          if (line.startsWith(':')) {
            continue
          }
        }
      }
    })
    .catch(err => {
      if (err.name === 'AbortError') {
        return
      }
      logger.error('SSE Stream Error:', err)
      if (onError) onError(err)
    })

  return {
    abort: () => controller.abort()
  }
}

export async function fetchCSRFToken() {
  try {
    await get('/csrf')
  } catch (err) {
    console.warn('Failed to fetch CSRF token:', err)
  }
}

export default {
  request,
  get,
  post,
  put,
  del,
  upload,
  fetchStream,
  fetchCSRFToken,
  clearTokens,
  signImageUrl
}
